import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, FileText, BadgeCheck, Archive, Phone, MessageCircle, Mail, Download } from "lucide-react";
import api, { download, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApi, PageHeader, StatusBadge, Loading, Card, Field, Select, Empty } from "../components/common";
import LeadForm from "../components/LeadForm";
import { QuoteDialog, ConvertDialog } from "../components/SalesDialogs";
import { Button } from "../components/ui/button";
import { fdate, fdatetime, inr } from "../lib/format";

const WON = "Won / Project Confirmed";

function ActivityBox({ lead, onSaved }) {
  const [f, setF] = useState({ type: "call", note: "", next_followup: "" });
  const save = async () => {
    try {
      await api.post(`/leads/${lead.id}/activities`, { ...f, next_followup: f.next_followup || null });
      toast.success("Activity logged");
      setF({ type: "call", note: "", next_followup: "" });
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="activity-form">
      <div className="grid grid-cols-2 gap-2">
        <Select testid="activity-type" options={["call", "whatsapp", "email", "meeting", "note", "followup"]} value={f.type} onChange={(v) => setF({ ...f, type: v })} />
        <input data-testid="activity-next-followup" type="date" className="vmw-input" value={f.next_followup} onChange={(e) => setF({ ...f, next_followup: e.target.value })} title="Schedule next follow-up" />
      </div>
      <textarea data-testid="activity-note" className="vmw-input h-20 py-2" placeholder="Call notes, client response, requirements…" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      <Button size="sm" className="vmw-gradient" onClick={save} disabled={!f.note.trim()} data-testid="activity-submit">Log activity</Button>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const { meta } = useAuth();
  const { data: l, loading, reload } = useApi(`/leads/${id}`);
  const [dlg, setDlg] = useState(null);
  if (loading && !l) return <Loading />;
  if (!l) return <Empty text="Lead not found or not accessible" />;
  const won = l.status === WON;
  const move = async (stage) => {
    let note = "";
    if (stage === "Lost") { note = window.prompt("Reason for losing this lead?") || ""; if (!note) return; }
    try { await api.post(`/leads/${id}/stage`, { stage, note, lost_reason: note }); toast.success(`Moved to ${stage}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const archive = async () => { await api.post(`/leads/${id}/archive?archived=${!l.archived}`); toast.success(l.archived ? "Restored" : "Archived"); reload(); };
  const icon = { call: Phone, whatsapp: MessageCircle, email: Mail };
  return (
    <div data-testid="lead-detail">
      <PageHeader title={l.client_name} subtitle={`${l.code} · ${l.studio_name || "No studio"} · ${l.city || ""}`} actions={<>
        <Button variant="outline" onClick={() => setDlg("edit")} data-testid="lead-edit-button"><Pencil className="h-4 w-4 mr-1" />Edit</Button>
        {!won && <Button variant="outline" onClick={() => setDlg("quote")} data-testid="lead-quote-button"><FileText className="h-4 w-4 mr-1" />Quotation</Button>}
        {!won && l.status !== "Lost" && <Button className="vmw-gradient" onClick={() => setDlg("convert")} data-testid="lead-convert-button"><BadgeCheck className="h-4 w-4 mr-1" />Confirm Deal</Button>}
        <Button variant="ghost" onClick={archive} data-testid="lead-archive-button"><Archive className="h-4 w-4 mr-1" />{l.archived ? "Unarchive" : "Archive"}</Button>
      </>} />
      {won && l.project_id && (
        <div className="vmw-card p-4 mb-4 border-emerald-200 bg-emerald-50 flex flex-wrap items-center gap-3 text-sm" data-testid="lead-won-banner">
          <BadgeCheck className="h-5 w-5 text-emerald-600" /> Deal confirmed on {fdate(l.won_date)} for <b>{inr(l.deal_value)}</b>.
          <Link className="text-primary font-semibold" to={`/projects/${l.project_id}`}>Open project →</Link>
          <Link className="text-primary font-semibold" to={`/clients/${l.client_id}`}>Open client →</Link>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card title="Pipeline stage" testid="lead-stage-card">
            <div className="flex flex-wrap gap-1.5">
              {meta?.lead_stages.filter((s) => s !== WON).map((s) => (
                <button key={s} disabled={won} onClick={() => move(s)} data-testid={`stage-btn-${s.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${l.status === s ? "vmw-gradient text-white border-transparent" : "hover:border-primary hover:text-primary"} disabled:opacity-50`}>{s}</button>
              ))}
            </div>
            <div className="mt-4 space-y-1.5 text-xs text-muted-foreground" data-testid="stage-history">
              {[...(l.stage_history || [])].reverse().map((h, i) => <div key={i}><b className="text-slate-700">{h.stage}</b> · {fdatetime(h.at)} · {h.by_name}{h.note ? ` · ${h.note}` : ""}</div>)}
            </div>
          </Card>
          <Card title="Communication history" testid="lead-activities">
            <ActivityBox lead={l} onSaved={reload} />
            <div className="mt-4 divide-y">
              {l.activities.map((a) => { const I = icon[a.type] || FileText; return (
                <div key={a.id} className="py-3 flex gap-3"><div className="h-8 w-8 rounded-full bg-violet-50 grid place-items-center shrink-0"><I className="h-4 w-4 text-primary" /></div>
                  <div><div className="text-sm whitespace-pre-wrap">{a.note}</div><div className="text-[11px] text-muted-foreground mt-0.5">{a.type} · {a.by_name} · {fdatetime(a.at)}{a.next_followup ? ` · next follow-up ${fdate(a.next_followup)}` : ""}</div></div></div>); })}
              {!l.activities.length && <Empty text="No activity logged yet" />}
            </div>
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="Lead details" testid="lead-info">
            <dl className="grid grid-cols-2 gap-y-2.5 text-sm">
              {[["Stage", <StatusBadge value={l.status} />], ["Priority", <StatusBadge value={l.priority} />], ["Contact", l.contact_person], ["Phone", l.phone], ["WhatsApp", l.whatsapp], ["Email", l.email],
                ["Source", l.source], ["Service", l.service], ["Est. sheets", l.est_sheets], ["Est. value", inr(l.est_value)], ["Next follow-up", fdate(l.next_followup)], ["Last contact", fdate(l.last_contact)], ["Created", fdate(l.created_date)]]
                .map(([k, v]) => <div key={k} className="contents"><dt className="text-muted-foreground text-xs">{k}</dt><dd className="font-medium break-words">{v || "—"}</dd></div>)}
            </dl>
            {l.notes && <p className="text-sm mt-3 p-3 rounded-lg bg-muted/60 whitespace-pre-wrap">{l.notes}</p>}
          </Card>
          {l.leadgen && (
            <Card title="Discovery (Lead Generation Agent)" testid="lead-discovery-card">
              <dl className="grid grid-cols-2 gap-y-2 text-sm">
                {[["Prospect", l.leadgen.prospect_code], ["Source", l.leadgen.source], ["First discovered", fdate(l.leadgen.first_discovered_at)],
                  ["Score", `${l.leadgen.score}/100`], ["Qualification", l.leadgen.qualification], ["Location", [l.leadgen.district, l.leadgen.state].filter(Boolean).join(", ")]]
                  .map(([k, v]) => <div key={k} className="contents"><dt className="text-muted-foreground text-xs">{k}</dt><dd className="font-medium break-words">{v || "—"}</dd></div>)}
              </dl>
              {l.leadgen.source_url?.startsWith("http") && <a href={l.leadgen.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary break-all block mt-2">{l.leadgen.source_url}</a>}
            </Card>
          )}
          <Card title="Quotations" testid="lead-quotations">
            {l.quotations.length ? l.quotations.map((q) => (
              <div key={q.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
                <div><div className="font-mono text-xs font-semibold">{q.code}</div><div className="text-xs text-muted-foreground">{fdate(q.date)}</div></div>
                <div className="flex items-center gap-2"><span className="font-num font-semibold">{inr(q.total)}</span><StatusBadge value={q.status} />
                  <button title="PDF" onClick={() => download(`/quotations/${q.id}/pdf`, `${q.code}.pdf`)} data-testid={`quote-pdf-${q.id}`}><Download className="h-4 w-4 text-slate-500 hover:text-primary" /></button></div>
              </div>)) : <Empty text="No quotations yet" />}
          </Card>
        </div>
      </div>
      <LeadForm open={dlg === "edit"} initial={l} onClose={() => setDlg(null)} onSaved={reload} />
      <QuoteDialog open={dlg === "quote"} lead={l} onClose={() => setDlg(null)} onSaved={reload} />
      <ConvertDialog open={dlg === "convert"} lead={l} onClose={() => setDlg(null)} onDone={reload} />
    </div>
  );
}
